import qs from 'qs';
import { API_DATE_FORMAT, getPastMonths, nowUTC } from 'src/utils/timeUtils';
import { DateUTC, UUID } from 'src/types/utility';
import { COMMON_COLORS } from '@neteera/neteera-web-nextgen';
import { MonitoringDaysData, TIMEFRAME_RANGE } from './types';
import { ManipulateType } from 'dayjs';
import {
  CustomerFeatureSettings,
  HPFeatureSettingResponse,
} from 'src/routes/Customers/modules/types';

export const POINTS_DATE_FORMAT = 'YYYY-MM-DDTHH:mm';
const BED_OCCUPANCY_DAYS_TIMEFRAME = 8;

enum TREND_ICONS {
  UP = 'ArrowUpwards',
  DOWN = 'ArrowDownwards',
}

export const extractQpPatientId = (): UUID | null => {
  const queryParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });

  return (queryParams.patientId || null) as UUID | null;
};

export const getBedOccupancyInterval = (): {
  startTime: DateUTC;
  endTime: DateUTC;
} => {
  const now = nowUTC();

  return {
    startTime: now
      .subtract(BED_OCCUPANCY_DAYS_TIMEFRAME, 'day')
      .startOf('day')
      .format(API_DATE_FORMAT),
    endTime: now.subtract(2, 'day').endOf('day').format(API_DATE_FORMAT),
  };
};

export const getStartTimeOfRange = (range: TIMEFRAME_RANGE): DateUTC => {
  const now = nowUTC();
  switch (range) {
    case TIMEFRAME_RANGE.DAYS_3:
      return now.subtract(3, 'day').toISOString();
    case TIMEFRAME_RANGE.WEEK:
      return now.subtract(1, 'week').toISOString();
    case TIMEFRAME_RANGE.DAY:
    default:
      return now.subtract(1, 'day').toISOString();
  }
};

export const getDefaultEndTime = (): DateUTC => nowUTC().toISOString();

// TODO: remove if not used
export const getGranularityOfRange = (
  range: TIMEFRAME_RANGE,
): { stepSize: number; unit: ManipulateType } => {
  switch (range) {
    case TIMEFRAME_RANGE.DAYS_3:
      return {
        stepSize: 3,
        unit: 'hour',
      };
    case TIMEFRAME_RANGE.WEEK:
      return {
        stepSize: 7,
        unit: 'hour',
      };
    case TIMEFRAME_RANGE.DAY:
    default:
      return {
        stepSize: 1,
        unit: 'hour',
      };
  }
};

export const generate12MonthOptions = (): Record<
  'label' | 'value',
  string
>[] => {
  const months = getPastMonths(12);

  return months.map(month => ({
    label: month,
    value: month,
  }));
};

export const getMonitoringDaysTrend = (
  currentMonthData: MonitoringDaysData,
  previousMonthData: MonitoringDaysData,
) => {
  const currentMonthPercentage =
    (currentMonthData.monitoredDaysCount * 100) / currentMonthData.totalDays;
  const previousMonthPercentage =
    (previousMonthData.monitoredDaysCount * 100) / previousMonthData.totalDays;

  return Math.round(currentMonthPercentage - previousMonthPercentage);
};
const MONITORING_DAYS_THRESHOLD = 16;

export const getMonitoringDaysTrendColor = (
  trendValue: number,
  monitoringDaysCount: number,
) => {
  if (trendValue > 0) {
    return COMMON_COLORS.greenLight;
  }
  if (monitoringDaysCount > MONITORING_DAYS_THRESHOLD || !trendValue) {
    return COMMON_COLORS.blueLight;
  }
  return COMMON_COLORS.redLight;
};
export const getMonitoringDaysTrendIconProps = (trendValue: number) => {
  if (!trendValue) {
    return undefined;
  }

  return {
    iconName: trendValue > 0 ? TREND_ICONS.UP : TREND_ICONS.DOWN,
  };
};

export const mergeHpCustomerFeatureSettings = (
  rawData: HPFeatureSettingResponse,
) =>
  rawData?.reduce((acc: Omit<CustomerFeatureSettings, 'customerId'>, item) => {
    acc[item.feature] = item.available;
    return acc;
  }, {});
